import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Clock, Mail, MapPin } from 'lucide-react';

const navLinks = [
  { name: 'Accueil', section: 'accueil' },
  { name: 'Services', section: 'services' },
  { name: 'Réalisations', section: 'realisations' },
  { name: 'Partenaires', section: 'partenaires' },
  { name: 'Contact', section: 'devis' }
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    setIsMobileMenuOpen(false);
    if (element) {
      requestAnimationFrame(() => element.scrollIntoView({ behavior: 'smooth' }));
    }
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Top bar - navy */}
      <div className="bg-[#01162D] text-white text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-[#9FB3CC] text-[11px] sm:text-xs md:text-sm whitespace-nowrap">
            <Clock size={14} className="text-[#3B8BEF] flex-shrink-0" />
            <span>Lun-Jeu : 8h-12h / 14h-18h</span>
            <span className="text-[#3B8BEF]">|</span>
            <span>Ven : 8h-12h</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden md:flex items-center gap-2 text-[#9FB3CC]">
              <MapPin size={14} className="text-[#3B8BEF]" />
              Saint-Étienne (42) · Loire
            </span>
            <a href="mailto:contact@sma-concept.fr" className="hidden lg:flex items-center gap-2 hover:text-[#3B8BEF] transition-colors" data-testid="header-email">
              <Mail size={14} className="text-[#3B8BEF]" />
              contact@sma-concept.fr
            </a>
          </div>
        </div>
      </div>

      {/* Main bar - white */}
      <div className={`bg-white transition-shadow duration-300 border-b border-[#E3EDF9] ${isScrolled ? 'shadow-lg' : ''}`}>
        <nav className="max-w-7xl mx-auto px-4 relative">
          <div className="flex items-center justify-between h-16 md:h-28">
            <div
              className="cursor-pointer flex items-center"
              onClick={() => scrollToSection('accueil')}
              data-testid="header-logo"
            >
              <img
                src="/logo-sma-concept.png"
                alt="SMA CONCEPT - Menuiserie Aluminium & Serrurerie"
                className="h-12 md:h-[84px] w-auto"
              />
            </div>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.section}
                  onClick={() => scrollToSection(link.section)}
                  className="font-display text-[#01162D] hover:text-[#1F6FD6] font-semibold text-sm uppercase tracking-wider transition-colors relative group"
                  data-testid={`nav-${link.section}`}
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#1F6FD6] group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:0620604166"
                className="flex items-center gap-2 text-[#01162D] font-bold hover:text-[#1F6FD6] transition-colors"
                data-testid="header-phone"
              >
                <span className="w-10 h-10 rounded-full bg-[#E3EDF9] flex items-center justify-center text-[#1F6FD6]">
                  <Phone size={18} />
                </span>
                06 20 60 41 66
              </a>
              <button
                onClick={() => scrollToSection('devis')}
                className="ml-2 bg-[#1F6FD6] hover:bg-[#01162D] text-white px-6 py-3 rounded-md font-display font-bold text-sm uppercase tracking-wider transition-colors duration-300 shadow-md"
                data-testid="header-cta-devis"
              >
                Demander un devis
              </button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#01162D] p-2 hover:bg-[#E3EDF9] rounded-md transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden absolute left-0 right-0 top-full bg-white shadow-xl px-4 pb-5 border-t border-[#E3EDF9] pt-3 max-h-[calc(100vh-110px)] overflow-y-auto" data-testid="mobile-menu">
              <ul className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <li key={link.section}>
                    <button
                      onClick={() => scrollToSection(link.section)}
                      className="text-[#01162D] hover:text-[#1F6FD6] hover:bg-[#EEF1F5] font-semibold w-full text-left py-3 px-2 rounded-md transition-colors"
                      data-testid={`mobile-nav-${link.section}`}
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
                <li className="pt-3">
                  <button
                    onClick={() => scrollToSection('devis')}
                    className="w-full bg-[#01162D] text-white px-6 py-3 rounded-md font-bold hover:bg-[#1F6FD6] transition-colors mb-3"
                    data-testid="mobile-cta-devis"
                  >
                    Demander un devis
                  </button>
                  <a
                    href="tel:0620604166"
                    className="w-full bg-[#1F6FD6] text-white px-6 py-3 rounded-md font-bold hover:bg-[#01162D] transition-colors flex items-center justify-center gap-2"
                    data-testid="mobile-phone"
                  >
                    <Phone size={18} />
                    06 20 60 41 66
                  </a>
                </li>
              </ul>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
